/**
 * Browser-side catalog injection for the PaCC prototype.
 *
 * Plugin-pathogen-database + plugin-region-database both have Node fs
 * loaders that don't work in browsers. Instead we use Vite's
 * import.meta.glob to bundle all JSON records at build time, then
 * inject via the plugins' browser-safe __setXxxRecords entry points.
 *
 * This runs once at module import (before any surface renders).
 */

import { __setPathogenRecords } from '@biomax/plugin-pathogen-database';
import { __setRegionRecords } from '@biomax/plugin-region-database';
import { __setBundledGenomes, parseBundledGenome } from '@biomax/plugin-pathogen-genomics';
import { __setSignalRecords, type IngestResult, type SourceReport, type LivenessSnapshot } from '@biomax/plugin-surveillance-feed';
import { __setOutbreakCorpus, type ReferenceOutbreak } from '@biomax/plugin-epi-validation';

// Eager glob: returns a record keyed by relative path with module values.
const pathogenModules = import.meta.glob<{ default: unknown }>(
  '../../../packages/plugin-pathogen-database/data/pathogens/*.json',
  { eager: true },
);
const regionModules = import.meta.glob<{ default: unknown }>(
  '../../../packages/plugin-region-database/data/regions/*.json',
  { eager: true },
);
// FASTAs imported as raw text via Vite's ?raw query.
const genomeFastas = import.meta.glob<string>(
  '../../../packages/plugin-pathogen-genomics/data/genomes/*.fasta',
  { eager: true, query: '?raw', import: 'default' },
);
const genomeMetas = import.meta.glob<{ default: { accession: string; sequenceLengthBp: number; fetchedAt: string } }>(
  '../../../packages/plugin-pathogen-genomics/data/genomes/*.json',
  { eager: true },
);

__setPathogenRecords(
  Object.entries(pathogenModules).map(([path, mod]) => ({
    sourcePath: path,
    raw: JSON.stringify(mod.default),
  })),
);

__setRegionRecords(
  Object.entries(regionModules).map(([path, mod]) => ({
    sourcePath: path,
    raw: JSON.stringify(mod.default),
  })),
);

__setBundledGenomes(
  Object.entries(genomeFastas).map(([fastaPath, fasta]) => {
    const pathogenId = fastaPath.split('/').pop()!.replace(/\.fasta$/, '');
    const metaPath = fastaPath.replace(/\.fasta$/, '.json');
    const meta = genomeMetas[metaPath]?.default;
    if (!meta) throw new Error(`missing genome metadata for ${pathogenId} at ${metaPath}`);
    return parseBundledGenome(pathogenId, fasta, meta);
  }),
);

// ---------------------------------------------------------------------------
// Validation corpus — observed historical-outbreak series, REAL DATA fetched
// from cited open sources (scripts/fetch-corpus.ts). Drives the §7.4c Model
// Validation surface (backtest scores + calibration vs observed). Verifier-
// first: every series carries a source + PMID that round-trips.
// ---------------------------------------------------------------------------
const outbreakModules = import.meta.glob<{ default: ReferenceOutbreak }>(
  '../../../packages/plugin-epi-validation/data/outbreaks/*.json',
  { eager: true },
);
__setOutbreakCorpus(Object.values(outbreakModules).map(m => m.default));

// ---------------------------------------------------------------------------
// Surveillance feed — REAL DATA ONLY. No seed, no fabricated fallback.
// `npm run ingest` writes signals.snapshot.json from the live open sources
// (CDC NWSS · WHO DON · CoV-Spectrum LAPIS). If no snapshot is present, the
// feed is simply EMPTY and the surfaces show an honest "no live data — run
// ingest" state. We never inject invented outbreak data.
// import.meta.glob returns {} when the optional file is absent.
// ---------------------------------------------------------------------------
const snapshotMods = import.meta.glob<{ default: IngestResult }>('./signals.snapshot.json', { eager: true });
const snapshot = Object.values(snapshotMods)[0]?.default;

__setSignalRecords(snapshot?.signals ?? []);

/** ISO time the live snapshot was ingested, or null when no snapshot present. */
export const FEED_GENERATED_AT: string | null = snapshot?.generatedAt ?? null;
/** Per-source ingest status from the snapshot (empty when no snapshot). */
export const FEED_SOURCES: SourceReport[] = snapshot?.sources ?? [];
/** True when real ingested data is present. */
export const FEED_IS_LIVE = snapshot != null;

// ---------------------------------------------------------------------------
// Liveness snapshot — the honest "N of M sources live RIGHT NOW" headline,
// produced by the 6-hourly ingest cron and committed (it's reachability
// metadata, not data). `generatedAt: ""` + total 0 = no scheduled run yet.
// ---------------------------------------------------------------------------
const livenessMods = import.meta.glob<{ default: LivenessSnapshot }>('./liveness.snapshot.json', { eager: true });
const livenessSnap = Object.values(livenessMods)[0]?.default;
/** Committed liveness snapshot; null-ish (total 0 / empty generatedAt) until
 *  the first scheduled ingest populates it. */
export const LIVENESS: LivenessSnapshot | null =
  livenessSnap && livenessSnap.generatedAt ? livenessSnap : null;

// ---------------------------------------------------------------------------
// Phase 7.2 — per-source 30-day history register. Written by scripts/ingest.ts
// on each ingest run. Drives the §7.22 Feed Health SourcesView 30-day strips
// from real ingest history when available; falls back to synthetic sample
// data otherwise (the strip is always rendered with exactly 30 cells).
// ---------------------------------------------------------------------------
import type { SourceHistory } from '@biomax/plugin-surveillance-feed';
import { EMPTY_HISTORY } from '@biomax/plugin-surveillance-feed';
const historyMods = import.meta.glob<{ default: SourceHistory }>('./signals.history.json', { eager: true });
export const FEED_HISTORY: SourceHistory = Object.values(historyMods)[0]?.default ?? EMPTY_HISTORY;
